import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import type { Color } from "@salestrak/ui";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { resolveWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { WidgetIssueStageDwellDocument } from "#app/lib/data";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";

/** How to summarize how long the covered projects dwelt in a stage. */
export type StageAgeMeasure = "avg" | "max";
export type StageAgeSort = "duration" | "alpha";

/** One bar — a stage, its summarized dwell in days, how many projects it
 * covers, and the stage's own palette color (charts wear it). */
export type StageAgeRow = { id: string; label: string; color: Color | null; days: number; count: number };

const SECONDS_PER_DAY = 86_400;

/**
 * Per-stage dwell for the duration bar card, from the server's `issueStageDwell`
 * aggregation: time-in-stage (average or maximum) across the covered projects,
 * measured from timestamped stage-transition activities. Folds the shared
 * scope + Filters window like the other count cards; a card never re-plumbs it.
 */
export function useStageAge(measure: StageAgeMeasure, sort: StageAgeSort, filters: unknown) {
  const { base, override } = resolveWidgetFilters(filters);
  const { where } = useScopedWhere(base, { override });
  const { stageMap } = useWorkspaceData();

  const variables = useMemo(() => ({ where }), [where]);
  const { data: queryData, loading, error } = useQuery(WidgetIssueStageDwellDocument, {
    variables,
    fetchPolicy: "cache-and-network",
  });

  const data = useMemo<StageAgeRow[]>(() => {
    const built = (queryData?.issueStageDwell ?? []).map((stat) => {
      const stage = stageMap.get(stat.stageId);
      return {
        id: stat.stageId,
        label: stage?.name ?? "Unknown stage",
        color: (stage?.icon?.color as Color) ?? null,
        days: Math.round((measure === "max" ? stat.maxSeconds : stat.avgSeconds) / SECONDS_PER_DAY),
        count: stat.issues,
      };
    });

    if (sort === "alpha") built.sort((a, b) => a.label.localeCompare(b.label));
    else built.sort((a, b) => b.days - a.days);
    return built;
  }, [queryData, measure, sort, stageMap]);

  return { data, loading: loading && !queryData, error };
}
